import { z } from 'zod';
import { DecimalLikeSchema, IsoDateTimeSchema, PaginationSchema, UuidSchema } from './common.js';
export const CajaEstadoSchema = z.enum(['abierta', 'cerrada']);
export const CajaSchema = z.object({
    id: UuidSchema,
    sucursalId: UuidSchema,
    saldo: DecimalLikeSchema.nullable(),
    estado: CajaEstadoSchema.nullable(),
    ultimoCierre: DecimalLikeSchema.nullable(),
    branchName: z.string().nullable(),
    createdAt: IsoDateTimeSchema.nullable(),
});
// Shape mínimo para o seletor de transferência cross-sucursal (GET /api/cajas/options).
// Não expõe saldo/estado/ultimoCierre de cajas de outras sucursais — só id + branchName,
// o necessário para escolher origem/destino. Ver ADR-023.
export const CajaOptionSchema = z.object({
    id: UuidSchema,
    branchName: z.string().nullable(),
});
export const CreateCajaRequestSchema = CajaSchema.omit({
    id: true,
    createdAt: true,
    saldo: true,
    estado: true,
    ultimoCierre: true,
}).extend({
    saldo: DecimalLikeSchema.nullable().optional(),
    branchName: z.string().nullable().optional(),
});
export const UpdateCajaRequestSchema = z.object({
    sucursalId: UuidSchema.optional(),
    branchName: z.string().nullable().optional(),
});
export const AperturaRequestSchema = z.object({
    saldoApertura: DecimalLikeSchema,
    saldoAperturaUsd: DecimalLikeSchema.nullable().optional(),
    tasaCambio: DecimalLikeSchema.nullable().optional(),
    observaciones: z.string().nullable().optional(),
});
export const CajaAperturaSchema = z.object({
    id: UuidSchema,
    cajaId: UuidSchema,
    saldoApertura: DecimalLikeSchema.nullable(),
    saldoAperturaUsd: DecimalLikeSchema.nullable(),
    tasaCambio: DecimalLikeSchema.nullable(),
    abiertoPor: UuidSchema.nullable(),
    observaciones: z.string().nullable(),
    createdAt: IsoDateTimeSchema.nullable(),
});
export const CierreRequestSchema = z.object({
    saldoFinal: DecimalLikeSchema,
    observaciones: z.string().nullable().optional(),
});
export const CajaCierreSchema = z.object({
    id: UuidSchema,
    cajaId: UuidSchema,
    fechaCierre: IsoDateTimeSchema,
    saldoInicial: DecimalLikeSchema.nullable(),
    ingresosTotal: DecimalLikeSchema.nullable(),
    egresosTotal: DecimalLikeSchema.nullable(),
    saldoFinal: DecimalLikeSchema,
    diferencia: DecimalLikeSchema.nullable(),
    observaciones: z.string().nullable(),
    cerradoPor: UuidSchema.nullable(),
    createdAt: IsoDateTimeSchema.nullable(),
});
export const CajasSummarySchema = z.object({
    saldoTotal: DecimalLikeSchema,
    abiertas: z.number().int(),
    cerradas: z.number().int(),
    topCajas: z.array(CajaSchema),
});
export const CajaEventTypeSchema = z.enum([
    'caja.opened',
    'caja.closed',
    'caja.balance_changed',
]);
export const CajaEventSchema = z.object({
    type: CajaEventTypeSchema,
    cajaId: UuidSchema,
    timestamp: IsoDateTimeSchema,
    payload: z.record(z.string(), z.unknown()),
});
export const WsTicketResponseSchema = z.object({
    ticket: z.string().min(16),
    expiresIn: z.number().int().positive(),
});
export const TransaccionTipoSchema = z.enum(['ingreso', 'egreso']);
export const TransaccionSchema = z.object({
    id: UuidSchema,
    cajaId: UuidSchema,
    usuarioId: UuidSchema.nullable(),
    tipo: TransaccionTipoSchema,
    monto: DecimalLikeSchema,
    concepto: z.string().nullable(),
    categoria: z.string().nullable(),
    fecha: IsoDateTimeSchema,
    receiptUrl: z.string().nullable(),
    receiptFilename: z.string().nullable(),
    receiptType: z.string().nullable(),
    createdAt: IsoDateTimeSchema.nullable(),
});
// Quando a categoria da transação é de combustible (egreso), o front anexa o veículo
// da flota e os litros carregados; o backend cria um GastoCombustible ligado à transação
// na mesma operação. O monto do gasto = monto da transação.
export const TransaccionGastoCombustibleSchema = z.object({
    vehiculoId: UuidSchema,
    litros: DecimalLikeSchema.nullable().optional(),
});
export const CreateTransaccionRequestSchema = z.object({
    cajaId: UuidSchema,
    tipo: TransaccionTipoSchema,
    monto: DecimalLikeSchema,
    fecha: IsoDateTimeSchema,
    concepto: z.string().max(500).nullable().optional(),
    categoria: z.string().max(80).nullable().optional(),
    gastoCombustible: TransaccionGastoCombustibleSchema.optional(),
});
export const UpdateTransaccionRequestSchema = z.object({
    tipo: TransaccionTipoSchema.optional(),
    monto: DecimalLikeSchema.optional(),
    fecha: IsoDateTimeSchema.optional(),
    concepto: z.string().max(500).nullable().optional(),
    categoria: z.string().max(80).nullable().optional(),
});
export const TransaccionListQuerySchema = PaginationSchema.extend({
    cajaId: UuidSchema.optional(),
    tipo: TransaccionTipoSchema.optional(),
    from: IsoDateTimeSchema.optional(),
    to: IsoDateTimeSchema.optional(),
});
// ---- Movimientos unificados (transacciones + transferencias) ----
// Vista de solo lectura que fusiona transacciones de caja con transferencias de
// tesorería (caja↔caja, efectivo↔caja, efectivo↔banco). Las transferencias NO
// generan Transaccion; este shape común permite listarlas juntas, ordenadas por fecha.
export const MovimientoKindSchema = z.enum(['transaccion', 'transferencia']);
export const MovimientoTipoSchema = z.enum(['ingreso', 'egreso', 'transferencia']);
export const MovimientoUnificadoSchema = z.object({
    id: UuidSchema,
    kind: MovimientoKindSchema,
    fecha: IsoDateTimeSchema,
    tipo: MovimientoTipoSchema,
    monto: DecimalLikeSchema,
    concepto: z.string().nullable(),
    categoria: z.string().nullable(),
    // transacción: caja única; transferencia: origen/destino (cualquiera puede ser null
    // cuando un extremo es tesorería — efectivo central o banco).
    cajaId: UuidSchema.nullable(),
    cajaOrigenId: UuidSchema.nullable(),
    cajaDestinoId: UuidSchema.nullable(),
    // tipo original de la transferencia (efectivo_a_caja, caja_a_caja, …); null en transacciones.
    transferenciaTipo: z.string().nullable(),
    // saldo de la caja DESPUÉS de este movimiento (running balance). Solo presente al
    // filtrar por una caja (`cajaId`); null cuando la vista mezcla varias cajas.
    saldoAcumulado: DecimalLikeSchema.nullable(),
});
export const MovimientoListQuerySchema = PaginationSchema.extend({
    cajaId: UuidSchema.optional(),
    tipo: MovimientoTipoSchema.optional(),
    from: IsoDateTimeSchema.optional(),
    to: IsoDateTimeSchema.optional(),
});
// ---- Reset financiero (operação destrutiva, Superadmin) ----
// Dupla confirmação: senha do usuário + palavra exata "RESETEAR".
export const ResetFinancieroRequestSchema = z.object({
    password: z.string().min(1),
    confirmacion: z.literal('RESETEAR'),
});
export const ResetFinancieroResultSchema = z.object({
    ok: z.literal(true),
    snapshotId: UuidSchema,
    eliminados: z.record(z.string(), z.number().int()),
});
//# sourceMappingURL=finanzas.js.map